import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import Papa from 'papaparse';
import JSZip from 'jszip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport } from '@fortawesome/free-solid-svg-icons';
import { CompassParticipant, PatientVisit, PatientVisitMetadata } from '../../amplify/api-gateway/search-patients';
import { 
  callQuery, 
  COMPASS_ALL_PATIENTS_ANNOTATIONS
} from "../../amplify/api-gateway/query";
import { 
  generateDates, 
  getDayTruncatedDate, 
  subtractHours, 
  withTimezone 
} from "../../utils/time";

interface SurveyData {
  eq_1?: string;
  eq_2?: string;
  eq_3?: string;
  eq_4?: string;
  eq_5?: string;
  eq_6?: string;
  pain_3?: string;
  se_c?: string;
  visit_1?: string;
  visit_2?: string;
  visit_3?: string;
  visit_4?: string;
  start_date?: string;
  end_date?: string;
}

export interface ProcessedPatientData {
  dateToSurveyAvailability: Record<string, SurveyData>;
  dailyKeyToData: Record<string, Record<string, number>>;
  dateToMonitorAvailability: Record<string, number>;
  dateToHospital: Record<string, number>;
  dateToED: Record<string, number>;
  dateToNonHospital: Record<string, number>;
  compassDates: Date[];
  studyId?: string;
  patientName?: string;
}

interface DataExportProps {
  // Either pass preprocessed data or raw patient data for fetching
  data: ProcessedPatientData | (CompassParticipant & PatientVisit & PatientVisitMetadata)[];
  variant?: "outline-secondary" | "primary";
  buttonText?: string;
}

const DataExport: React.FC<DataExportProps> = ({ 
  data,
  variant = "outline-secondary",
  buttonText
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  const processAllPatientData = (rawData: any[]): Record<string, ProcessedPatientData> => {
    const result: Record<string, ProcessedPatientData> = {};
    
    for (let row of rawData) {
      if (!row.patient_data) continue;
      
      const patientData = row.patient_data;
      const infoParticipant = patientData.participant_details; 
      if (!infoParticipant?.study_id) continue;
      
      const studyId = infoParticipant.study_id;
      
      // Calculate date range
      let endDate = withTimezone(
          infoParticipant.end_date ? new Date(infoParticipant.end_date) : new Date(), 
          "America/Los_Angeles"
      );
      const tPlus31Date = withTimezone(
          subtractHours(new Date(infoParticipant.enrollment_date), -24 * 30), 
          "America/Los_Angeles"
      );
      endDate = endDate > tPlus31Date ? endDate : tPlus31Date;
      const compassDates = generateDates(
          getDayTruncatedDate(withTimezone(new Date(infoParticipant.enrollment_date), "America/Los_Angeles")),
          getDayTruncatedDate(endDate)
      );

      // Process annotations
      const timeToMonitor: Record<string, number> = {};
      const timeToHospitalVisit: Record<string, number> = {};
      const timeToEDVisit: Record<string, number> = {};
      const timeToNonHospitalVisit: Record<string, number> = {};
      const dailyKeyToData: Record<string, Record<string, number>> = {};

      for (let row of (patientData.annotations || [])) {
        if (!Array.isArray(row) || row.length < 3) continue;
        
        const dt = withTimezone(new Date(row[0]), "America/Los_Angeles").toISOString().split("T")[0];
        if (!dt) continue;

        const value = Number(row[2]);
        if (isNaN(value)) continue;

        switch(row[1]) {
            case "WEARING_DETECTION":
                timeToMonitor[dt] = value;
                break;
            case "VISIT_HOSPITAL":
                timeToHospitalVisit[dt] = value;
                break;
            case "VISIT_ED":
                timeToEDVisit[dt] = value;
                break;
            case "VISIT_NON_HOSPITAL":
                timeToNonHospitalVisit[dt] = value;
                break;
            default:
                if (!dailyKeyToData[row[1]]) {
                    dailyKeyToData[row[1]] = {};
                }
                dailyKeyToData[row[1]][dt] = value;
            }
        }

        // Process surveys
        const dateToSurveyAvailability: Record<string, SurveyData> = {};
        for (let row of (patientData.surveys || [])) {
            if (!row?.start_date) continue;
            const dt = withTimezone(new Date(row.start_date), "America/Los_Angeles").toISOString().split("T")[0];
            if (!dt) continue;
            dateToSurveyAvailability[dt] = row;
        }

        result[studyId] = {
            dateToSurveyAvailability,
            dailyKeyToData,
            dateToMonitorAvailability: timeToMonitor,
            dateToHospital: timeToHospitalVisit,
            dateToED: timeToEDVisit,
            dateToNonHospital: timeToNonHospitalVisit,
            compassDates,
            studyId,
            patientName: infoParticipant.mrn
        };
    }
    
    return result;
  };

  const generatePatientCSV = (data: ProcessedPatientData) => {
    const allData: Record<string, any> = {};
    
    data.compassDates.forEach((date: Date) => {
      const dateStr = date.toISOString().split('T')[0];
      const surveyData = data.dateToSurveyAvailability[dateStr] || {};
      
      allData[dateStr] = {
        date: dateStr,
        eq_1: surveyData.eq_1 || '',
        eq_2: surveyData.eq_2 || '',
        eq_3: surveyData.eq_3 || '',
        eq_4: surveyData.eq_4 || '',
        eq_5: surveyData.eq_5 || '',
        eq_6: surveyData.eq_6 || '',
        pain_3: surveyData.pain_3 || '',
        se_c: surveyData.se_c || '',
        visit_1: surveyData.visit_1 || '',
        visit_2: surveyData.visit_2 || '',
        visit_3: surveyData.visit_3 || '',
        visit_4: surveyData.visit_4 || '',
        monitor_availability: data.dateToMonitorAvailability[dateStr] || null,
        hospital_visit: data.dateToHospital[dateStr] ? 1 : 0,
        ed_visit: data.dateToED[dateStr] ? 1 : 0,
        non_hospital_visit: data.dateToNonHospital[dateStr] ? 1 : 0,
      };

      Object.keys(data.dailyKeyToData).forEach(metric => {
        allData[dateStr] = {
          ...allData[dateStr],
          [metric]: data.dailyKeyToData[metric][dateStr] || null
        };
      });
    });

    return Papa.unparse(Object.values(allData));
  };

  const handleExport = async () => {
    setIsExporting(true);
    setProgress(0);
    
    try {
      // Single patient
      if (!Array.isArray(data)) {
        const csvData = generatePatientCSV(data);
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = data.studyId ? `${data.studyId}_data.csv` : 'patient_data.csv';
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return;
      }
   
      // For multiple patients, use bulk query
      const patientsData = await callQuery({
        operation: COMPASS_ALL_PATIENTS_ANNOTATIONS
      }).then(processAllPatientData);
      
      const zip = new JSZip();
      const dataFolder = zip.folder("patient_data");
      
      if (dataFolder) {
        const selectedPatientIds = data.map(p => p.study_id);        
        const selectedPatientsData = Object.entries(patientsData)
          .filter(([studyId]) => selectedPatientIds.includes(studyId))
          .map(([_, data]) => data);   

          for (let i = 0; i < selectedPatientsData.length; i++) {
          const patientData = selectedPatientsData[i];
          const csvData = generatePatientCSV(patientData);          
          const fileName = `${patientData.studyId || `patient_${i + 1}`}_data.csv`;
          dataFolder.file(fileName, csvData);
          setProgress(Math.round(((i + 1) / selectedPatientsData.length) * 100));
        }
      }
      
      const content = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'compass_patient_data.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
   
    } catch (error) {
      console.error('Error exporting data:', error);
    } finally {
      setIsExporting(false);
      setProgress(0);
    }
   };

  const defaultButtonText = Array.isArray(data) ? 'Export All Data' : 'Export Data';

  return (
    <Button
      variant={variant}
      size="sm"
      onClick={handleExport}
      disabled={isExporting}
      className={Array.isArray(data) ? "trackboard-quick-actions-btn" : ""}
    >
      {isExporting ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="me-2"
          />
          Exporting {progress}%
        </>
      ) : (
        <>
          <FontAwesomeIcon icon={faFileExport} className="me-1" />
          {buttonText || defaultButtonText}
        </>
      )}
    </Button>
  );
};

export default DataExport;